import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useImages from '@hooks/useImages'
import useSelector from '@hooks/useSelector'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import * as campaignComparableActions from '@redux/modules/campaignComparable'

import defaultFormState, { requiredFields } from '@models/campaignComparable'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type {
  CampaignComparableModel, CampaignComparableRequestOptions, PropertySuggestionModel,
} from '@models/types'

const stringifyObjectKeys: (keyof CampaignComparableModel)[] = ['data']

type CreateCampaignComparableParams = {
  campaignComparableParams: Partial<CampaignComparableModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignComparableRequestOptions,
}

const createCampaignComparable = (params: CreateCampaignComparableParams) => {
  const { campaignComparableParams, dispatch, requestOptions } = params
  const { createCampaignComparable: createFn } = campaignComparableActions

  const updatedParams = mergeAndStringifyEntityObjects({}, campaignComparableParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateCampaignComparableParams = {
  campaignComparable: CampaignComparableModel,
  campaignComparableParams: Partial<CampaignComparableModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignComparableRequestOptions,
}

const updateCampaignComparable = (params: UpdateCampaignComparableParams) => {
  const {
    campaignComparable, campaignComparableParams, dispatch, requestOptions,
  } = params
  const { updateCampaignComparable: updateFn } = campaignComparableActions

  const updatedParams = mergeAndStringifyEntityObjects(
    campaignComparable,
    campaignComparableParams,
    stringifyObjectKeys,
  )

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCampaignComparableParams = {
  campaignComparable: DeleteParams<CampaignComparableModel>,
  dispatch: AppDispatch,
}

const deleteCampaignComparable = (params: DeleteCampaignComparableParams) => {
  const { dispatch, campaignComparable } = params
  const { deleteCampaignComparable: deleteFn } = campaignComparableActions

  return dispatch(deleteFn(campaignComparable))
}

type CreateComparableFromDataProviderParams = {
  campaignId: number,
  dispatch: AppDispatch,
  property: PropertySuggestionModel,
  requestOptions?: CampaignComparableRequestOptions,
}

const createComparableFromDataProvider = (params: CreateComparableFromDataProviderParams) => {
  const {
    campaignId, dispatch, property, requestOptions,
  } = params
  const { createComparableFromDataProvider: createFn } = campaignComparableActions
  const { data_provider, id } = property

  return dispatch(createFn(campaignId, data_provider, id, requestOptions))
}

export function useCampaignComparableImage(campaignComparable: CampaignComparableModel) {
  const { default_image, id, source_campaign_id } = campaignComparable

  const { filteredImages } = useImages({
    filters: {
      imageable_id: id,
      imageable_type: 'CampaignComparable',
    },
  })

  const image = filteredImages.length ? filteredImages[0] : {}
  // @ts-expect-error TS2339
  const imageUrl = source_campaign_id ? default_image : image?.thumb_url

  return {
    image,
    imageUrl,
  }
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignComparableForm(
  campaignComparable: Partial<CampaignComparableModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignComparableForm = useForm(
    defaultFormState,
    { entity: campaignComparable, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignComparable.id, campaignComparable.cache_key],
  )

  return {
    ...campaignComparableForm,
  }
}

function useCampaignComparable(initEntity: Partial<CampaignComparableModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: campaignComparable }: { entity: CampaignComparableModel } = useLatestEntity(initEntity, 'campaignComparables')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignComparables)

  return {
    callbacks: {
      addCampaignComparable: () => launchModal({
        callbacks,
        modalKey: 'AddCampaignComparableModal',
        payload: { campaignComparable },
      }),
      browseCampaignComparables: () => launchModal({
        callbacks,
        modalKey: 'BrowseCampaignComparablesModal',
        payload: { campaignComparable },
      }),
      createCampaignComparable: (
        campaignComparableParams: Partial<CampaignComparableModel>,
        entityOptions?: CampaignComparableRequestOptions,
      ) => (
        createCampaignComparable({ campaignComparableParams, dispatch, requestOptions: entityOptions })
      ),
      createComparableFromDataProvider: (
        property: PropertySuggestionModel,
        entityOptions?: CampaignComparableRequestOptions,
      ) => (
        createComparableFromDataProvider({
          campaignId: campaignComparable.campaign_id, dispatch, property, requestOptions: entityOptions,
        })
      ),
      createOrEditCampaignComparable: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignComparableModal',
        payload: { campaignComparable },
      }),
      deleteCampaignComparable: () => deleteCampaignComparable({ dispatch, campaignComparable }),
      searchCampaignComparables: () => launchModal({
        callbacks,
        modalKey: 'SearchCampaignComparablesModal',
        payload: { campaignComparable },
      }),
      updateCampaignComparable: (
        campaignComparableParams: Partial<CampaignComparableModel>,
        entityOptions?: CampaignComparableRequestOptions,
      ) => (
        updateCampaignComparable({
          campaignComparable, campaignComparableParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaignComparable,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignComparable.id),
    updating,
  }
}

export type CampaignComparablePayload = ReturnType<typeof useCampaignComparable>

export default useCampaignComparable
